import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
// import MDEditor from '@uiw/react-md-editor';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  TextField,
  Rating,
  Skeleton,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckIcon,
  PlayArrow as PlayIcon,
  Quiz as QuizIcon,
  VideoLibrary as VideoIcon,
  Article as ArticleIcon,
  NavigateNext as NavigateNextIcon,
  NavigateBefore as NavigateBeforeIcon
} from '@mui/icons-material';

import trainingService from '@/services/trainingService';
import {
  TrainingGuide,
  TrainingProgress,
  TrainingSection,
  QuizResult
} from '@/types/training';

const TrainingGuideViewer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [guide, setGuide] = useState<TrainingGuide | null>(null);
  const [progress, setProgress] = useState<TrainingProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [quizOpen, setQuizOpen] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState<Record<string, string>>({});
  const [completionDialogOpen, setCompletionDialogOpen] = useState(false);
  const [rating, setRating] = useState<number | null>(null);
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    if (id) {
      loadTrainingGuide();
    }
  }, [id]);

  const loadTrainingGuide = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);

      const [guideData, progressData] = await Promise.all([
        trainingService.getTrainingGuide(id),
        trainingService.getTrainingProgress(id)
      ]);

      setGuide(guideData);
      setProgress(progressData);

      // Set current section based on progress
      if (progressData.currentSection) {
        const sectionIndex = guideData.content.sections.findIndex(
          section => section.id === progressData.currentSection
        );
        if (sectionIndex >= 0) {
          setCurrentSectionIndex(sectionIndex);
        }
      }
    } catch (err) {
      setError('Failed to load training guide');
      console.error('Error loading training guide:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartTraining = async () => {
    if (!id || !guide) return;

    try {
      const updatedProgress = await trainingService.startTraining(id);
      setProgress(updatedProgress);
    } catch (err) {
      console.error('Error starting training:', err);
    }
  };

  const handleSectionComplete = async (sectionId: string) => {
    if (!id || !progress) return;

    try {
      const updatedProgress = await trainingService.completeSection(id, sectionId, progress);
      setProgress(updatedProgress);

      // Check if all sections are completed
      if (guide && updatedProgress.completedSections.length === guide.content.sections.length) {
        setCompletionDialogOpen(true);
      }
    } catch (err) {
      console.error('Error completing section:', err);
    }
  };

  const handleQuizSubmit = async (sectionId: string, results: QuizResult) => {
    if (!id || !progress) return;

    try {
      const updatedProgress = await trainingService.submitQuizResults(id, sectionId, results, progress);
      setProgress(updatedProgress);
      setQuizOpen(false);
      
      // Mark section as complete after quiz
      await handleSectionComplete(sectionId);
    } catch (err) {
      console.error('Error submitting quiz:', err);
    }
  };

  const handleCompleteTraining = async () => {
    if (!id) return;

    try {
      await trainingService.completeTraining(id, rating || undefined, feedback);
      setCompletionDialogOpen(false);
      navigate('/training');
    } catch (err) {
      console.error('Error completing training:', err);
    }
  };

  const navigateToSection = (index: number) => {
    if (guide && index >= 0 && index < guide.content.sections.length) {
      setCurrentSectionIndex(index);
    }
  };

  const getSectionIcon = (section: TrainingSection) => {
    switch (section.type) {
      case 'video':
        return <VideoIcon />;
      case 'quiz':
        return <QuizIcon />;
      case 'interactive':
        return <PlayIcon />;
      default:
        return <ArticleIcon />;
    }
  };

  const isSectionCompleted = (sectionId: string) => {
    return progress?.completedSections.includes(sectionId) || false;
  };

  const getProgressPercentage = () => {
    if (!guide || !progress) return 0;
    return (progress.completedSections.length / guide.content.sections.length) * 100;
  };

  const QuizDialog: React.FC<{ section: TrainingSection }> = ({ section }) => {
    if (!section.quiz) return null;

    const handleQuizAnswerChange = (questionId: string, answer: string) => {
      setQuizAnswers(prev => ({
        ...prev,
        [questionId]: answer
      }));
    };

    const handleQuizSubmitLocal = () => {
      const questions = section.quiz!.questions;
      let correct = 0;
      
      questions.forEach(question => {
        if (quizAnswers[question.id] === question.correctAnswer) {
          correct++;
        }
      });

      const score = (correct / questions.length) * 100;
      const results: QuizResult = {
        score,
        answers: quizAnswers,
        completedAt: new Date().toISOString()
      };

      handleQuizSubmit(section.id, results);
      setQuizAnswers({});
    };

    return (
      <Dialog open={quizOpen} onClose={() => setQuizOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{section.title}</DialogTitle>
        <DialogContent>
          {section.quiz.questions.map((question) => (
            <Box key={question.id} sx={{ mb: 3 }}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend">{question.question}</FormLabel>
                {question.type === 'multiple-choice' && question.options && (
                  <RadioGroup
                    value={quizAnswers[question.id] || ''}
                    onChange={(e) => handleQuizAnswerChange(question.id, e.target.value)}
                  >
                    {question.options.map((option) => (
                      <FormControlLabel
                        key={option}
                        value={option}
                        control={<Radio />}
                        label={option}
                      />
                    ))}
                  </RadioGroup>
                )}
                {question.type === 'true-false' && (
                  <RadioGroup
                    value={quizAnswers[question.id] || ''}
                    onChange={(e) => handleQuizAnswerChange(question.id, e.target.value)}
                  >
                    <FormControlLabel value="true" control={<Radio />} label="True" />
                    <FormControlLabel value="false" control={<Radio />} label="False" />
                  </RadioGroup>
                )}
                {question.type === 'text' && (
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={quizAnswers[question.id] || ''}
                    onChange={(e) => handleQuizAnswerChange(question.id, e.target.value)}
                    placeholder="Enter your answer..."
                  />
                )}
              </FormControl>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuizOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleQuizSubmitLocal} 
            variant="contained"
            disabled={section.quiz.questions.some(q => !quizAnswers[q.id])}
          >
            Submit Quiz
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 3 }}>
          <Skeleton variant="text" height={40} width="60%" />
          <Skeleton variant="text" height={20} width="40%" sx={{ mt: 1 }} />
          <Skeleton variant="rectangular" height={200} sx={{ mt: 3 }} />
          <Skeleton variant="rectangular" height={400} sx={{ mt: 2 }} />
        </Box>
      </Container>
    );
  }

  if (error || !guide) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 3 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error || 'Training guide not found'}
          </Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/training')}
          >
            Back to Training
          </Button>
        </Box>
      </Container>
    );
  }

  const currentSection = guide.content.sections[currentSectionIndex];
  const hasStarted = progress?.status !== 'not-started';

  return (
    <Container maxWidth="lg">
      {/* Header */}
      <Box sx={{ mt: 3, mb: 4 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
          <Link color="inherit" href="/training" sx={{ cursor: 'pointer' }}>
            Training
          </Link>
          <Typography color="text.primary">{guide.title}</Typography>
        </Breadcrumbs>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              {guide.title}
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {guide.description}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Chip label={guide.category} size="small" />
              <Chip label={guide.difficulty} size="small" color="primary" />
              <Chip label={`${guide.estimatedDuration}min`} size="small" variant="outlined" />
            </Box>
          </Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/training')}
            variant="outlined"
          >
            Back to Training
          </Button>
        </Box>

        {/* Progress Bar */}
        {hasStarted && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Progress
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Math.round(getProgressPercentage())}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={getProgressPercentage()}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
        )}
      </Box>

      {!hasStarted ? (
        /* Getting Started */
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Ready to start your training?
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            This training will take approximately {guide.estimatedDuration} minutes to complete.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<PlayIcon />}
            onClick={handleStartTraining}
          >
            Start Training
          </Button>
        </Paper>
      ) : (
        /* Training Content */
        <Box sx={{ display: 'flex', gap: 3 }}>
          {/* Section Navigation */}
          <Box sx={{ width: 300, flexShrink: 0 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Sections
              </Typography>
              <Stepper orientation="vertical" activeStep={currentSectionIndex}>
                {guide.content.sections.map((section, index) => (
                  <Step key={section.id} completed={isSectionCompleted(section.id)}>
                    <StepLabel 
                      icon={getSectionIcon(section)}
                      onClick={() => navigateToSection(index)}
                      sx={{ cursor: 'pointer' }}
                    >
                      {section.title}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Paper>
          </Box>

          {/* Main Content */}
          <Box sx={{ flexGrow: 1 }}>
            <Card>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5">
                    {currentSection.title}
                  </Typography>
                  {isSectionCompleted(currentSection.id) && (
                    <Chip 
                      icon={<CheckIcon />} 
                      label="Completed" 
                      color="success" 
                      variant="outlined" 
                    />
                  )}
                </Box>

                {/* Section Content */}
                <Box sx={{ mb: 4 }}>
                  <textarea>{currentSection.content}</textarea>
                </Box>

                {/* Section Actions */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      startIcon={<NavigateBeforeIcon />}
                      onClick={() => navigateToSection(currentSectionIndex - 1)}
                      disabled={currentSectionIndex === 0}
                    >
                      Previous
                    </Button>
                    <Button
                      endIcon={<NavigateNextIcon />}
                      onClick={() => navigateToSection(currentSectionIndex + 1)}
                      disabled={currentSectionIndex === guide.content.sections.length - 1}
                    >
                      Next
                    </Button>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {currentSection.type === 'quiz' && currentSection.quiz && (
                      <Button
                        variant="contained"
                        startIcon={<QuizIcon />}
                        onClick={() => setQuizOpen(true)}
                        disabled={isSectionCompleted(currentSection.id)}
                      >
                        {isSectionCompleted(currentSection.id) ? 'Quiz Completed' : 'Take Quiz'}
                      </Button>
                    )}
                    {currentSection.type !== 'quiz' && (
                      <Button
                        variant="contained"
                        startIcon={<CheckIcon />}
                        onClick={() => handleSectionComplete(currentSection.id)}
                        disabled={isSectionCompleted(currentSection.id)}
                      >
                        {isSectionCompleted(currentSection.id) ? 'Completed' : 'Mark Complete'}
                      </Button>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* Quiz Dialog */}
      {currentSection.type === 'quiz' && (
        <QuizDialog section={currentSection} />
      )}

      {/* Completion Dialog */}
      <Dialog open={completionDialogOpen} onClose={() => setCompletionDialogOpen(false)}>
        <DialogTitle>Congratulations!</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            You've completed the training: <strong>{guide.title}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Please rate your experience and provide any feedback.
          </Typography>
          
          <Box sx={{ my: 3 }}>
            <Typography component="legend">Rating</Typography>
            <Rating
              name="training-rating"
              value={rating}
              onChange={(_, newValue) => setRating(newValue)}
            />
          </Box>

          <TextField
            fullWidth
            multiline
            rows={4}
            label="Feedback (optional)"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="How was your training experience? Any suggestions for improvement?"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompletionDialogOpen(false)}>Skip</Button>
          <Button onClick={handleCompleteTraining} variant="contained">
            Complete Training
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TrainingGuideViewer;